<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { useGridRef, useRouteColumnFilter, useODataSource, useDefaultGridOptions, useDefaultExport, refreshGridAsync, getCreatedRowId, refreshChangedRowAsync } from '@/composables/data-grid'
import { useEntityChanges } from '@/composables/entity-changes'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'

import { planningApi } from '@/apis/planning.api'
import { usePortalStore, entityChangeTypeEnum } from '@/stores/portal.store'
import { usePlanningStore } from '@/stores/planning.store'

import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TagCellEditTemplate from '@/components/shared/TagCellEditTemplate.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import GroupEdit from '@/components/shared/GroupEdit.vue'
import GroupsGridDetail from '@/components/planner/GroupsGridDetail.vue'

const gridId = 'groups-grid'

const i18n = getI18n()
const portalStore = usePortalStore()
const planningStore = usePlanningStore()
const editItem = ref(null)

let localizedServiceTags = planningStore.getLocalizedServiceTags()
const getPrimaryServiceTags = () => localizedServiceTags

const { GROUP_PHYSICAL, GROUP_VIRTUAL } = portalStore.serviceStructureTypeEnum
const groupStructureTypes = portalStore.getLocalizedServiceStructureTypes().filter(t =>
  t.id === GROUP_PHYSICAL || t.id === GROUP_VIRTUAL
)

const { gridRef } = useGridRef()
const { onContentReady } = useRouteColumnFilter(gridRef)
const { getServiceTagChips, getServiceStructureTypeChip } = useChips()
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.group', 2) })


const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.groupsUrl, {
  customParamsFn: () => showArchived.value ? { show_archived: true } : {},
})

const { archiveWarning, archiveButton, showArchived, archiveAsync, onRowPrepared: onArchiveRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { await planningStore.archiveGroupAsync(id, { skipErrorMessage: true }) },
  onDelete: async(id, config) => { await planningStore.deleteGroupAsync(id, config) },
  gridRef: gridRef,
})

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      {
        name: 'edit',
        visible: (e) => !e.row?.data?.archived,
        onClick: (e) => { editItem.value = { ...e.row.data } },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      {
        name: 'clone',
        icon: 'copy',
        visible: (e) => !e.row?.data?.archived,
        onClick: async(e) => {
          await planningStore.cloneGroupAsync(e.row.data.id)
          await gridRef.value.instance.refresh()
        },
      },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    width: '12rem',
  },
  dataGridColumn.useAvatarColumn(),
  { dataField: 'code', caption: i18n.t('common.code'), width: '10rem', sortOrder: 'asc', sortIndex: 1 },
  { dataField: 'name', caption: i18n.t('common.name'), validationRules: [{ type: 'required' }], width: '14rem' },
  dataGridColumn.useStructureTypeColumn({ dataSource: groupStructureTypes, width: '10rem', sortOrder: 'asc', sortIndex: 0 }),
  { dataField: 'description', caption: i18n.t('common.description'), width: '16rem' },
  dataGridColumn.useServiceTagsColumn({
    dataSource: getPrimaryServiceTags(),
    getTagsFn: getPrimaryServiceTags,
    cellTemplate: 'primary-service-tags-cell-template',
    editCellTemplate: 'primary-service-tags-cell-edit-template',
  }),
  ...dataGridColumn.useRateCardColumns(),
  {
    dataField: 'service_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate: 'fileViewer',
    editCellTemplate: 'fileUploader',
    width: '16rem',
    allowExporting: false,
  },
  dataGridColumn.useArchivedAtColumn(),
  dataGridColumn.useArchivedColumn(),
]

const onSaved = async() => {
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  localizedServiceTags = planningStore.getLocalizedServiceTags()
  await gridRef.value.instance.refresh()
}

const onDownloadServiceFileAsync = async(e) => {
  if (e) await planningStore.downloadServiceFileAsync(e.id, e.name)
}

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns: columns,
  dataSource: gridDataSource,
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  editing: {
    mode: 'popup',
    allowUpdating: false,
    allowAdding: false,
    allowDeleting: true,
    useIcons: true,
  },
  onSaved: onSaved,
  onContentReady: onContentReady,
  onRowPrepared: onArchiveRowPrepared,
  onRowRemoving: onRowRemoving,
  onExporting: onExporting,
  export: exportOptions,
  clearFilterButton: true,
  archivedToggle: true,
  addButton: {
    onClick: () => { editItem.value = {} },
  },
})

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  localizedServiceTags = planningStore.getLocalizedServiceTags()
  editItem.value = null
  await refreshGridAsync(gridRef, createdId)
}

useEntityChanges([entityChangeTypeEnum.GROUP], (change) => refreshChangedRowAsync(gridRef, change), () => editItem.value !== null)
</script>

<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <group-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <groups-grid-detail :group="templateData.data" />
      </template>
      <template #structure-type-cell-template="{ data: templateOptions }">
        <chip-template :values="getServiceStructureTypeChip(templateOptions.data.structure_type)" />
      </template>
      <template #primary-service-tags-cell-template="{ data: templateOptions }">
        <chip-template
          v-if="templateOptions.data.tags?.length"
          :values="getServiceTagChips(templateOptions.data.tags)"
        />
      </template>
      <template #primary-service-tags-cell-edit-template="{ data: templateOptions }">
        <tag-cell-edit-template
          :value="templateOptions.data.tags"
          :data-source="getPrimaryServiceTags()"
          @value-changed="(e) => templateOptions.setValue(e.value)"
        />
      </template>
      <template #fileUploader="{ data: templateOptions }">
        <file-url-edit
          :cell-info="templateOptions"
          @download="onDownloadServiceFileAsync"
        />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
